/**
 * 고객 FCM 웹푸시
 *
 * @CODE:CUSTOMER-WEBPUSH | SPEC: docs/superpowers/specs/2026-06-11-customer-realtime-fix-and-webpush-design.md
 */
import { getMessaging, getToken, onMessage, isSupported, type Messaging } from 'firebase/messaging';
import { toast } from 'sonner';
import { pushApi } from './api/push';
import { apiClient } from './api/client';
import { isEchoConnected } from './echo';
import { initializeFirebase } from './firebase';
import { logger } from '@/utils/logger';

const DISMISS_STORAGE_KEY = 'olulo_push_optin_dismissed_at';
const DISMISS_COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000;
const TOKEN_STORAGE_KEY = 'olulo_push_registered_token';

/** 로그아웃 해지가 in-flight 등록 합류를 기다리는 상한 — 로그아웃 UI 블로킹 방지 */
const REGISTRATION_JOIN_TIMEOUT_MS = 3000;

let echoNotificationsActive = false;

/**
 * 등록 세대(epoch) 카운터 — 로그아웃과 in-flight 토큰 갱신의 레이스 차단.
 *
 * 앱 진입 직후 fire-and-forget refreshPushToken() 이 getToken 을 기다리는 사이
 * 로그아웃의 unregisterPushToken() 이 먼저 끝나면, 늦게 resolve 된 갱신이 이전
 * 고객의 토큰을 다시 등록/저장한다. unregister 시작 시 epoch 를 올려 그 시점
 * 이전에 시작된 등록 경로를 await 경계마다 무효화한다.
 */
let pushEpoch = 0;

/** NotificationsContext 가 채널 구독 성공/해제 시점에 호출 — FCM 포그라운드 억제는 Echo 수신이 실제 보장될 때만 */
export function setEchoNotificationsActive(active: boolean): void {
  echoNotificationsActive = active;
}

export interface PushOptInState {
  supported: boolean;
  permission: NotificationPermission;
  dismissedAt: number | null;
}

/** 권한 안내 배너 노출 여부 (순수 함수) */
export function shouldShowPushOptIn(state: PushOptInState): boolean {
  if (!state.supported || state.permission !== 'default') return false;
  if (state.dismissedAt !== null && Date.now() - state.dismissedAt < DISMISS_COOLDOWN_MS) return false;
  return true;
}

export async function isPushSupported(): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  if (!('serviceWorker' in navigator)) return false;
  if (!('Notification' in window)) return false;
  try {
    return await isSupported();
  } catch {
    return false;
  }
}

export function dismissPushOptIn(): void {
  try {
    localStorage.setItem(DISMISS_STORAGE_KEY, String(Date.now()));
  } catch {
    // localStorage 접근 불가 — 조용히 무시
  }
}

export function getDismissedAt(): number | null {
  try {
    const value = localStorage.getItem(DISMISS_STORAGE_KEY);
    if (!value) return null;
    const parsed = Number(value);
    return isNaN(parsed) ? null : parsed;
  } catch {
    return null;
  }
}

/**
 * SW 등록(설정 쿼리스트링 주입) → FCM 토큰 발급 — enablePush/refreshPushToken/unregisterPushToken 공통 경로.
 * firebase-js-sdk 는 빈 vapidKey 를 조용히 legacy 기본 키로 폴백하므로, 호출 전 VAPID 가드 필수.
 */
async function acquireToken(): Promise<{ token: string; messaging: Messaging } | null> {
  const firebaseConfigParam = encodeURIComponent(JSON.stringify({
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
  }));

  const registration = await navigator.serviceWorker.register(
    `/firebase-messaging-sw.js?config=${firebaseConfigParam}`
  );

  const firebaseApp = initializeFirebase();
  if (!firebaseApp) {
    logger.error('[Push] Firebase app not initialized');
    return null;
  }

  const messaging = getMessaging(firebaseApp);
  const token = await getToken(messaging, {
    vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
    serviceWorkerRegistration: registration,
  });

  if (!token) return null;
  return { token, messaging };
}

/** 진행 중인 등록 — 로그아웃 해지가 세션이 살아 있는 동안 합류 지점으로 사용한다 */
let inFlightRegistration: Promise<boolean> | null = null;

/**
 * 마지막 등록 토큰의 메모리 폴백 — localStorage.setItem 이 실패(쿼터/저장소 차단)해도
 * 같은 세션의 로그아웃 해지가 백엔드 DELETE 를 수행할 수 있게 한다 (codex 7차 P2).
 */
let lastRegisteredToken: string | null = null;

/** 등록을 시작하고 in-flight 추적에 연결한다 (enablePush/refreshPushToken 공통 진입점) */
function trackRegistration(epoch: number): Promise<boolean> {
  const registration = registerCurrentToken(epoch).finally(() => {
    if (inFlightRegistration === registration) {
      inFlightRegistration = null;
    }
  });
  inFlightRegistration = registration;
  return registration;
}

/**
 * 토큰 발급 → 백엔드 등록 → 포그라운드 핸들러 장착 (enablePush/refreshPushToken 공통)
 *
 * @param epoch - 호출 시작 시점에 캡처한 pushEpoch. await 경계를 지날 때마다
 *                현재 값과 비교해, 그 사이 로그아웃이 시작됐으면 등록을 중단한다.
 */
async function registerCurrentToken(epoch: number): Promise<boolean> {
  const authEpoch = apiClient.getAuthIdentityEpoch();
  const acquired = await acquireToken();
  if (!acquired) return false;

  // getToken 대기 중 로그아웃됨 — 이전 고객 토큰 재등록 금지
  if (epoch !== pushEpoch || authEpoch !== apiClient.getAuthIdentityEpoch()) return false;

  await pushApi.registerToken(acquired.token);

  // An already-dispatched POST cannot be undone safely after identity changes:
  // do not issue compensation with a different account or overwrite its local token.
  if (authEpoch !== apiClient.getAuthIdentityEpoch()) return false;

  // registerToken 대기 중 로그아웃됨 — 백엔드에 방금 등록된 토큰을 보상 해지한다
  // (로컬 저장만 막으면 백엔드 토큰이 잔존해 로그아웃 고객에게 푸시가 계속 간다 — codex 4차 P2).
  // 로그아웃 해지(unregisterPushToken)가 in-flight 등록(이 promise)을 await 한 뒤에야
  // 세션 정리로 넘어가므로, 이 보상 DELETE 는 세션이 살아 있는 동안 확정된다 (codex 5차 P2).
  if (epoch !== pushEpoch) {
    try {
      await pushApi.unregisterToken(acquired.token);
    } catch (error) {
      logger.error('[Push] 로그아웃 경합 토큰 보상 해지 실패:', error);
    }
    return false;
  }

  // 로그아웃 해지(unregisterPushToken)는 getToken 재호출 없이 이 저장/메모리 토큰을 사용한다
  lastRegisteredToken = acquired.token;
  try {
    localStorage.setItem(TOKEN_STORAGE_KEY, acquired.token);
  } catch {
    // localStorage 접근 불가 — 메모리 폴백(lastRegisteredToken)이 같은 세션 해지를 보장
  }
  attachForegroundHandler(acquired.messaging);
  return true;
}

/** 권한 요청 → SW 등록(설정 쿼리스트링 주입) → FCM 토큰 발급 → 백엔드 등록 */
export async function enablePush(): Promise<boolean> {
  try {
    const epoch = pushEpoch;


    // 빈 vapidKey 는 firebase-js-sdk 가 조용히 legacy 기본 키로 폴백해
    // 잘못된 키쌍으로 토큰을 발급한다 — 미설정 배포를 감지 가능하게 차단.
    if (!import.meta.env.VITE_FIREBASE_VAPID_KEY) {
      logger.error('[Push] VITE_FIREBASE_VAPID_KEY 미설정 — 푸시 활성화 불가');
      return false;
    }

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      dismissPushOptIn();
      return false;
    }

    return await trackRegistration(epoch);
  } catch (error) {
    logger.error('[Push] enablePush failed:', error);
    return false;
  }
}

/**
 * 비회원 푸시 귀속 컨텍스트 (#694 Part C)
 *
 * - order: 픽업 비회원 — 주문 단위 귀속, guest_password 가 등록 증명 (결정 1)
 * - table-session: 테이블 비회원 — 세션 단위 귀속 (결정 2).
 *   sessionToken 은 주문 완료 시점에 스냅샷 — 등록 시점에 세션이 만료되거나
 *   다른 QR 로 교체돼 sessionStorage 가 변해도 올바른 세션에 귀속된다 (#694 P2).
 */
export type GuestPushContext =
  | { kind: 'order'; orderId: string | number; guestPassword: string }
  | { kind: 'table-session'; sessionToken: string };

/**
 * 토큰 발급 → 게스트 귀속 등록 API 분기 → 포그라운드 핸들러 장착.
 *
 * 계정 경로(registerCurrentToken)와 달리 epoch 가드/로그아웃 해지/localStorage
 * 저장을 적용하지 않는다 — 게스트 토큰은 계정 귀속이 아니라 주문/세션 수명으로
 * 백엔드가 정리(종료 상태 도달·세션 close·48h TTL)하므로 로그아웃 해지가 불필요하다.
 */
async function registerGuestToken(context: GuestPushContext): Promise<boolean> {
  const acquired = await acquireToken();
  if (!acquired) return false;

  if (context.kind === 'order') {
    await pushApi.registerGuestOrderToken(context.orderId, acquired.token, context.guestPassword);
  } else {
    await pushApi.registerTableSessionToken(acquired.token, context.sessionToken);
  }

  attachForegroundHandler(acquired.messaging);
  return true;
}

/** 비회원: 권한 default → 배너 수락 시 호출 (권한 요청 포함) */
export async function enableGuestPush(context: GuestPushContext): Promise<boolean> {
  try {
    // 빈 vapidKey 는 firebase-js-sdk 가 조용히 legacy 기본 키로 폴백 — 계정 경로와 동일 가드.
    if (!import.meta.env.VITE_FIREBASE_VAPID_KEY) {
      logger.error('[Push] VITE_FIREBASE_VAPID_KEY 미설정 — 게스트 푸시 활성화 불가');
      return false;
    }

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      dismissPushOptIn();
      return false;
    }

    return await registerGuestToken(context);
  } catch (error) {
    logger.error('[Push] enableGuestPush failed:', error);
    return false;
  }
}

/** 비회원: 권한 이미 granted → 주문 완료 시 배너 없이 자동 재귀속 (결정 3, fire-and-forget 안전) */
export async function registerGuestPushIfGranted(context: GuestPushContext): Promise<void> {
  try {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    if (!import.meta.env.VITE_FIREBASE_VAPID_KEY) return;
    if (!(await isPushSupported())) return;

    await registerGuestToken(context);
  } catch (error) {
    logger.error('[Push] registerGuestPushIfGranted failed:', error);
  }
}

/** 앱 부팅 시 — 이미 권한이 있는 로그인 고객의 토큰을 재발급/재등록하고 포그라운드 핸들러를 장착한다 (FCM 토큰은 회전/만료됨) */
export async function refreshPushToken(): Promise<void> {
  try {
    const epoch = pushEpoch;

    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    if (!import.meta.env.VITE_FIREBASE_VAPID_KEY) return;
    if (!(await isPushSupported())) return;

    await trackRegistration(epoch);
  } catch (error) {
    logger.error('[Push] refreshPushToken failed:', error);
  }
}

/**
 * 로그아웃 시 마지막으로 등록한 토큰을 백엔드에서 해지. 로그아웃을 막지 않도록 실패는 삼킨다.
 *
 * getToken 재호출 대신 등록 시 저장한 토큰을 사용한다 — 빈 VAPID 배포에서 getToken 이
 * legacy 키 폴백 토큰을 반환해 실제 등록 토큰이 백엔드에 남는 문제를 차단하고,
 * 권한이 회수된 뒤에도 백엔드 토큰을 지울 수 있다 (저장 토큰 유무가 단일 기준).
 */
export async function unregisterPushToken(ownsIntent: () => boolean = () => true): Promise<void> {
  const authEpoch = apiClient.getAuthIdentityEpoch();
  const isCurrent = () => ownsIntent() && authEpoch === apiClient.getAuthIdentityEpoch();
  if (!isCurrent()) return;
  const registeredToken = lastRegisteredToken;
  let storedToken: string | null = null;
  try { storedToken = localStorage.getItem(TOKEN_STORAGE_KEY); } catch { /* memory fallback */ }
  // in-flight refreshPushToken/enablePush 무효화 — 로그아웃 뒤 이전 고객 토큰 재등록 차단
  pushEpoch++;

  // 진행 중인 등록이 있으면 끝날 때까지 합류 — epoch 가드로 등록이 스스로 중단되거나
  // 보상 해지한다. 로그아웃 흐름이 이 함수를 await 한 뒤 세션을 정리하므로, 합류를
  // 기다려야 보상 DELETE 가 살아 있는 세션에서 확정된다 (codex 5차 P2).
  // 단, getToken/SW 등록이 네트워크 문제로 장기 pending 이면 로그아웃 UI 가 멈추므로
  // 대기를 상한으로 제한한다 — 초과 시 best-effort 로 진행하고, 잔여 등록은 epoch 가드의
  // 보상 해지(또는 다음 고객 로그인 시 upsert 소유 이전)가 보정한다 (codex 8차 P2).
  if (inFlightRegistration) {
    try {
      await Promise.race([
        inFlightRegistration,
        new Promise<void>((resolve) => {
          setTimeout(resolve, REGISTRATION_JOIN_TIMEOUT_MS);
        }),
      ]);
    } catch {
      // 등록 실패는 해지에 영향 없음
    }
  }

  if (!isCurrent()) return;
  try {
    // 같은 세션 등록분(메모리)이 현재 세션의 진실 — setItem 실패로 localStorage 에 이전
    // 토큰이 남아 있을 수 있으므로 메모리를 우선한다 (codex 7·9차 P2). 둘이 다르면
    // stale 저장 토큰도 함께 해지해 어느 쪽도 백엔드에 잔존하지 않게 한다.
    const tokenToRevoke = registeredToken ?? storedToken ?? lastRegisteredToken;
    if (!tokenToRevoke) return;

    if (!isCurrent()) return;
    await pushApi.unregisterToken(tokenToRevoke);
    if (!isCurrent()) return;
    if (lastRegisteredToken === tokenToRevoke) lastRegisteredToken = null;
    if (storedToken !== null && storedToken !== tokenToRevoke) {
      try {
        if (!isCurrent()) return;
        await pushApi.unregisterToken(storedToken);
      } catch (error) {
        if (!isCurrent()) return;
        logger.error('[Push] stale 저장 토큰 해지 실패:', error);
      }
    }
    if (!isCurrent()) return;
    try {
      const currentToken = localStorage.getItem(TOKEN_STORAGE_KEY);
      if (currentToken === tokenToRevoke || currentToken === storedToken) localStorage.removeItem(TOKEN_STORAGE_KEY);
    } catch {
      // localStorage 접근 불가 — 조용히 무시
    }
  } catch (error) {
    if (!isCurrent()) return;
    logger.error('[Push] unregisterPushToken failed:', error);
  }
}

let foregroundAttached = false;

/**
 * 포그라운드 수신 — Echo 수신이 실제로 보장될 때만 Echo 토스트에 양보하고 스킵.
 *
 * 두 조건을 모두 요구한다 (codex 2·3차 리뷰):
 * - subscribed 플래그(echoNotificationsActive): 채널 인증/구독 실패 감지 — 소켓이
 *   connected 여도 private 채널 인증이 실패하면 Echo 토스트가 오지 않는다.
 * - connection.state(isEchoConnected): 구독 성공 후 소켓 끊김 감지 — subscribed 는
 *   1회 발화 후 disconnected/unavailable 이 돼도 subscription_error 로 재발화하지
 *   않아 플래그만으로는 끊김을 알 수 없다.
 *
 * FCM 페이로드 data 에는 notification id 가 없어 messageId 로는 Echo 토스트와
 * dedup 이 불가능하다 — 경계 시점에 드물게 같은 내용 토스트가 2개 표시될 수
 * 있으나 무해하다.
 */
function attachForegroundHandler(messaging: Messaging): void {
  if (foregroundAttached) return;
  foregroundAttached = true;
  onMessage(messaging, (payload) => {
    if (echoNotificationsActive && isEchoConnected()) return;
    const title = payload.notification?.title;
    if (title) toast(title, { description: payload.notification?.body });
  });
}
